import { Inject, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

import { BehaviorSubject, EMPTY, interval, Subject } from 'rxjs';
import { auditTime, debounceTime, filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { LOCATION_TOKEN, UPDATE_SERVICE_AUTO_DELAY } from '@app/injection-tokens';
import { SummitNotificationService } from '@summit/services';

@Injectable({
  providedIn: 'root'
})
export class UpdateService {
  protected notificationService: SummitNotificationService;
  protected disposing$ = new Subject();
  updateAvailable = new BehaviorSubject<boolean>(false);
  updateAvailableDebounced = false;

  constructor(
    private swUpdate: SwUpdate,
    private router: Router,
    @Inject(LOCATION_TOKEN) private locationObject: Location,
    @Inject(UPDATE_SERVICE_AUTO_DELAY) private DelayBeforeReload: number
  ) {
    this.notificationService = inject(SummitNotificationService);
    console.log(`[UpdateService] constructor`, this.swUpdate.isEnabled);
    if (!this.swUpdate.isEnabled) {
      console.log('[UpdateService] Service Worker Update Service not enabled');
    } else {
      console.log('[UpdateService]Service Worker Update Service is enabled');
      this.swUpdate.versionUpdates
        .pipe(
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
          map((evt) => ({
            type: 'UPDATE_AVAILABLE',
            current: evt.currentVersion,
            available: evt.latestVersion
          }))
        )
        .subscribe((_evt) => {
          // No Unsubscribe
          // an update is available
          console.trace('[UpdateService] New version detected via versionUpdates');
          this.updateAvailable.next(true);

          this.notificationService.triggerToast('Update Available', null, 'info', null, null, null, true);
        });

      // Periodically check for updates every 10 minutes
      interval(10 * 60 * 1000)
        .pipe(
          startWith(0), // Check immediately on startup
          switchMap(() => this.swUpdate.checkForUpdate()),
          takeUntil(this.disposing$)
        )
        .subscribe({
          next: (updateFound) => {
            console.trace('[UpdateService] Looked for a new version');
            if (updateFound) {
              console.trace('[UpdateService] New version detected via periodic check');
              this.updateAvailable.next(true);
            }
          },
          error: (err) => {
            console.error('[UpdateService] Error checking for updates:', err);
          }
        });
      this.updateAvailable
        .pipe(
          auditTime(16),
          debounceTime(this.DelayBeforeReload),
          filter((status) => status == true),
          takeUntil(this.disposing$)
        )
        .subscribe((debouncedUpdate) => {
          this.updateAvailableDebounced = debouncedUpdate;
        });
      this.router.events
        .pipe(
          //NavigationStart reloaded you on the page you were leaving
          filter((event) => event instanceof NavigationEnd),
          takeUntil(this.disposing$)
        )
        .subscribe(() => {
          if (this.updateAvailableDebounced) {
            this.update();
          }
        });
    }
  }

  async update() {
    await this.notificationService.showProgressSpinner(true, 'Updating');
    // console.log(`Cleaning up`);
    setTimeout(() => {
      this.cleanup();
      this.locationObject.reload();
    }, 1000);
  }

  cleanup() {
    this.disposing$.next(EMPTY);
    this.disposing$.complete();
  }

  OnDestroy() {
    this.cleanup();
  }

  logStart() {
    // console.log(`[UpdateService] started`);
  }
}
